import { z } from 'zod'
import { isoDateTime, localeSchema, uuid } from './common.js'

/**
 * Consent capture — build unit 7.12.
 *
 * Health, dietary and accessibility information is sensitive under LGPD
 * Art. 5 II, and Art. 11 offers no execution-of-contract basis for holding it.
 * It therefore needs its own recorded consent moment rather than a checkbox
 * buried in terms of service — this contract IS that moment.
 *
 * `travel_document` is in the DB enum (`consent_purpose`) for a later unit
 * that may want the same infrastructure; this build unit records a statement
 * for it neither in `en.ts`/`pt-BR.ts` nor here, so `recordableConsentPurpose`
 * is deliberately narrower than the full enum.
 */

export const consentPurpose = z.enum(['health', 'dietary', 'accessibility', 'travel_document'])
export type ConsentPurpose = z.infer<typeof consentPurpose>

export const recordableConsentPurpose = z.enum(['health', 'dietary', 'accessibility'])
export type RecordableConsentPurpose = z.infer<typeof recordableConsentPurpose>

export const consentVia = z.enum(['advisor_recorded', 'traveller_form'])
export type ConsentVia = z.infer<typeof consentVia>

/**
 * `statement` is the wording the person agreed to — a fixed, purpose-specific
 * disclosure sentence, not the sensitive data itself. The data lives in
 * `participant_sensitive_notes` (see `sensitiveNotes.ts`), gated by whether an
 * active consent for that purpose exists; the two used to be conflated inside
 * `statement` because that table did not exist yet. See `consent.service.ts`
 * for the rendering and `sensitiveNotes.service.ts` for the gate.
 *
 * `locale` picks which language's statement template is rendered. It is the
 * locale the advisor's screen was actually showing, so the stored sentence
 * matches what they read before recording it.
 */
export const createConsentRequest = z.object({
  purpose: recordableConsentPurpose,
  locale: localeSchema,
})
export type CreateConsentRequest = z.infer<typeof createConsentRequest>

export const consent = z.object({
  id: uuid,
  tripId: uuid,
  participantId: uuid,
  purpose: consentPurpose,
  grantedAt: isoDateTime,
  grantedVia: consentVia,
  /**
   * The exact wording shown at the time. **Null once withdrawn** — the row is
   * kept (LGPD Art. 16 legal-defence retention, same reasoning as
   * `travel_documents`), but this field is redacted at the one place every
   * route reads it through. `sensitiveNote.body` follows the same rule for
   * the data this consent actually authorises.
   */
  statement: z.string().nullable(),
  withdrawnAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type Consent = z.infer<typeof consent>
